import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { planName } from '../../lib/planCatalog';
import { daysLeftLabel, isTrialUrgent, trialPauseSentence } from '../../lib/trial';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useWorkspaceUsage } from '../../hooks/useWorkspaceUsage';
import { usePlanGateStore } from '../../store/planGate';
import { useAuthStore } from '../../store/auth';

function useCount(key: 'webhooks' | 'api-keys', enabled: boolean, workspaceId: string | null) {
  return useQuery({
    queryKey: [key, workspaceId],
    queryFn: async () => {
      const res = await api.get<Record<string, unknown>>(`/workspaces/${workspaceId}/${key}`, { skipGateDialog: true });
      const list = res.data[key === 'webhooks' ? 'webhooks' : 'keys'];
      return Array.isArray(list) ? list as Array<Record<string, unknown>> : [];
    },
    enabled: enabled && !!workspaceId,
    retry: false,
    staleTime: 60_000,
  });
}

/** Persistent "Pro trial · 9 days left · See plans" in the top bar; amber in the last days. */
export function TrialPill() {
  const theme = useTheme();
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const openPlanPicker = usePlanGateStore((s) => s.openPlanPicker);
  const { workspaceId, workspace } = useWorkspace();
  const usage = useWorkspaceUsage().data;
  const trial = usage?.trial?.active ? usage.trial : null;
  const urgent = isTrialUrgent(trial);
  // Only the last days name what pauses, so only then are keys and webhooks looked up.
  const keys = useCount('api-keys', urgent && !isEmbedded, workspaceId);
  const webhooks = useCount('webhooks', urgent && !isEmbedded, workspaceId);

  if (isEmbedded || !trial) return null;

  const name = planName(trial.tier);
  const settings = workspace?.whiteLabelSettings ?? null;
  const pauses = trialPauseSentence({
    publicKeyExists: !!keys.data?.some((key) => String(key['type']).toUpperCase() === 'PUBLIC' && !key['revokedAt']),
    // Logos and colours stay on Starter; only hidden ResponseDock branding pauses.
    brandingCustomised: !!settings?.hidePlatformBranding,
    webhookCount: webhooks.data?.length ?? 0,
  });
  const color = urgent ? theme.palette.warning.main : theme.palette.primary.main;
  const days = daysLeftLabel(trial.daysLeft);
  const label = `${name} trial, ${days}. ${urgent ? `${pauses} ` : ''}See plans`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }} data-testid="trial-pill" data-urgent={urgent ? 'true' : 'false'}>
      <Tooltip title={urgent ? pauses : `${name} features are unlocked until ${new Date(trial.endsAt).toLocaleDateString()}. No card needed.`}>
        <ButtonBase
          onClick={() => openPlanPicker({ highlightTier: trial.tier })}
          aria-label={label}
          sx={{
            borderRadius: 999,
            px: 1.5,
            py: 0.6,
            gap: 0.75,
            border: '1px solid',
            borderColor: alpha(color, 0.5),
            bgcolor: alpha(color, urgent ? 0.16 : 0.1),
            color: urgent ? theme.palette.warning.dark : theme.palette.primary.dark,
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
            '&:hover': { bgcolor: alpha(color, urgent ? 0.24 : 0.16) },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{name} trial · {days}</Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{name} trial · {trial.daysLeft}d</Box>
          <Box component="span" aria-hidden sx={{ opacity: 0.5 }}>·</Box>
          <Box component="span" sx={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>See plans</Box>
        </ButtonBase>
      </Tooltip>
      {urgent ? (
        <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700, display: { xs: 'none', lg: 'block' } }} noWrap>
          {pauses}
        </Typography>
      ) : null}
    </Box>
  );
}
